/**
 * Kilogram brand marks.
 *
 *   <Brandmark />  full "kilogram" wordmark, for login + brand moments.
 *   <Monomark />   "kg" mark with triangle, for sidebar header + mobile bar.
 *   <Weight />     the triangle alone, for favicons / tiny accents.
 *
 * Each component takes a `variant` (black / white) and an optional
 * className so callers can tune sizing.
 */
import type { CSSProperties } from 'react';
import { classNames } from '@/lib/format';

type Variant = 'black' | 'white';

const SRC: Record<'brand' | 'mono' | 'weight', Record<Variant, string>> = {
  brand: {
    black: '/brand/KG_Brandmark_Black.png',
    white: '/brand/KG_Brandmark_White.png',
  },
  mono: {
    black: '/brand/KG_Monomark_Black.png',
    white: '/brand/KG_Monomark_White.png',
  },
  weight: {
    black: '/brand/KG_Weight_Black.png',
    white: '/brand/KG_Weight_White.png',
  },
};

export function Brandmark({
  variant = 'black',
  className,
  style,
}: {
  variant?: Variant;
  className?: string;
  style?: CSSProperties;
}) {
  // Intrinsic dimensions of the trimmed wordmark PNG. The browser
  // uses these to reserve space before the bytes load, so the
  // surrounding row doesn't reflow when the image arrives. Layout
  // stays stable; CSS still controls actual rendered size.
  return (
    <img
      src={SRC.brand[variant]}
      alt="Kilogram"
      width={1561}
      height={363}
      className={classNames('select-none', className)}
      style={style}
      draggable={false}
    />
  );
}

export function Monomark({
  variant = 'black',
  className,
}: {
  variant?: Variant;
  className?: string;
}) {
  return (
    <img
      src={SRC.mono[variant]}
      alt="Kilogram"
      className={classNames('select-none', className)}
      draggable={false}
    />
  );
}

export function Weight({
  variant = 'black',
  className,
}: {
  variant?: Variant;
  className?: string;
}) {
  return (
    <img
      src={SRC.weight[variant]}
      alt=""
      aria-hidden="true"
      className={classNames('select-none', className)}
      draggable={false}
    />
  );
}
